import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { apiClient } from '../client';
import { processLayoutKeys } from '../query-keys';
import { invalidateDraftCaches } from './helpers/draftMutation';
import type {
  GetProcessLayoutResponse,
  ProcessLayoutScope,
  UpdateProcessLayoutPatch,
  UpdateProcessLayoutResponse
} from '@bliks-io/contracts';

/**
 * Upsert the layout doc for `{processId, scope}`. Only keys present in the
 * patch are written; `stepPositions` entries are upserted per node, every
 * other provided array replaces its counterpart wholesale. Optimistically
 * applies the patch to the cached layout the same way so drag-end and
 * swimlane reorder feel instantaneous; rolls back on error.
 */
export function useUpdateProcessLayout(processId: string, scope: ProcessLayoutScope) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (patch: UpdateProcessLayoutPatch): Promise<UpdateProcessLayoutResponse> => {
      const { data } = await apiClient.put<UpdateProcessLayoutResponse>(
        `/api/v2/process/${processId}/layout`,
        { scope, patch }
      );
      return data;
    },
    onMutate: async (patch) => {
      const { prev } = await optimisticLayoutUpdate(queryClient, processId, scope, (old) => ({
        ...old,
        ...(patch.stepPositions !== undefined
          ? { stepPositions: upsertStepPositions(old.stepPositions, patch.stepPositions) }
          : {}),
        ...(patch.generatedNodePositions !== undefined
          ? { generatedNodePositions: patch.generatedNodePositions }
          : {}),
        ...(patch.swimlaneOrder !== undefined ? { swimlaneOrder: patch.swimlaneOrder } : {}),
        ...(patch.laneColors !== undefined ? { laneColors: patch.laneColors } : {}),
        ...(patch.edgeLayouts !== undefined ? { edgeLayouts: patch.edgeLayouts } : {}),
        updatedAt: new Date()
      }));
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(processLayoutKeys.detail(processId, scope), ctx.prev);
      }
    },
    onSettled: () => {
      // A draft-scoped layout edit (node drag / swimlane reorder / edge-waypoint
      // move) captures a draft-history entry on the server (ADR 0007), advancing
      // the undo/redo cursor and truncating any redo tail. invalidateDraftCaches
      // refreshes the draft layout AND the draft detail — the latter carries the
      // toolbar's `history` meta (canUndo/canRedo/cursor), which would otherwise
      // stay stale until a full page reload. Published-scope edits don't capture,
      // so they only refresh their own layout.
      if (scope === 'draft') {
        void invalidateDraftCaches(queryClient, processId);
        return;
      }
      queryClient.invalidateQueries({
        queryKey: processLayoutKeys.detail(processId, scope)
      });
    }
  });
}

function upsertStepPositions(
  cached: GetProcessLayoutResponse['stepPositions'],
  updates: NonNullable<UpdateProcessLayoutPatch['stepPositions']>
): GetProcessLayoutResponse['stepPositions'] {
  const updatedIds = new Set(updates.map((p) => p.stableStepId));
  return [...cached.filter((p) => !updatedIds.has(String(p.stableStepId))), ...updates];
}

async function optimisticLayoutUpdate(
  queryClient: QueryClient,
  processId: string,
  scope: ProcessLayoutScope,
  updater: (prev: GetProcessLayoutResponse) => GetProcessLayoutResponse
): Promise<{ prev: GetProcessLayoutResponse | undefined }> {
  const key = processLayoutKeys.detail(processId, scope);
  await queryClient.cancelQueries({ queryKey: key });
  const prev = queryClient.getQueryData<GetProcessLayoutResponse>(key);
  if (prev) {
    queryClient.setQueryData<GetProcessLayoutResponse>(key, updater(prev));
  }
  return { prev };
}
